import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Download, Smartphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function isMobile() {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
}

export default function DeepLinkRedirect() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const [attempted, setAttempted] = useState(false);
  const [event, setEvent] = useState(null);

  const deepLink = `arcanapp://${type}/${id}`;
  const playStoreUrl = 'https://play.google.com/store';
  const appStoreUrl = 'https://apps.apple.com';

  useEffect(() => {
    // Fetch event info for display
    const endpoint = type === 'tournament'
      ? `${API_URL}/api/public/tournaments/${id}`
      : `${API_URL}/api/public/availabilities/${id}`;
    axios.get(endpoint).then(r => setEvent(r.data)).catch(() => {});
  }, [type, id]);

  useEffect(() => {
    if (!isMobile()) return;

    // Try to open the app via deep link
    const start = Date.now();
    window.location.href = deepLink;

    // If app didn't open (user comes back), redirect to store after timeout
    const timer = setTimeout(() => {
      if (Date.now() - start < 3000) {
        // App likely opened
        return;
      }
      setAttempted(true);
      // Redirect to appropriate store
      if (/Android/i.test(navigator.userAgent)) {
        window.location.href = playStoreUrl;
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href = appStoreUrl;
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [deepLink]);

  const mobile = isMobile();

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" data-testid="deep-link-page">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Trophy className="w-8 h-8 text-[#FF6B35]" />
          <span className="text-2xl font-bold text-white"><span className="text-[#FF6B35]">Arcan</span>app</span>
        </div>

        {mobile ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-[#FF6B35]" />
            </div>
            <h1 className="text-xl font-bold text-white">Ouverture de l'application...</h1>
            <p className="text-[#B0B0B0] text-sm">
              {attempted
                ? "L'application ne semble pas installée. Téléchargez-la pour accéder à cet événement."
                : "Tentative d'ouverture dans l'application Arcanapp..."}
            </p>
            <div className="flex flex-col gap-3 pt-4">
              <a href={playStoreUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-medium" data-testid="dl-play-store">
                <Download className="w-4 h-4" /> Télécharger sur Play Store
              </a>
              <a href={appStoreUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#252525] border border-[#333] text-white font-medium" data-testid="dl-app-store">
                <Download className="w-4 h-4" /> Télécharger sur App Store
              </a>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-white">
              {event?.name || "Événement Arcanapp"}
            </h1>
            {event && (
              <p className="text-[#B0B0B0] text-sm">
                {event.description?.slice(0, 200) || 'Consultez cet événement sur Arcanapp'}
              </p>
            )}
            <div className="flex flex-col gap-3 pt-4">
              <Link to={`/events/${type}/${id}`}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-medium" data-testid="view-on-web">
                Voir sur le web
              </Link>
              <p className="text-[#666] text-xs mt-2">
                Téléchargez l'application pour une meilleure expérience
              </p>
              <div className="flex gap-3 justify-center">
                <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#B0B0B0] hover:text-white flex items-center gap-1">
                  <Download className="w-3 h-3" /> Play Store
                </a>
                <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#B0B0B0] hover:text-white flex items-center gap-1">
                  <Download className="w-3 h-3" /> App Store
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
